import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
} from "@mui/material";
import { PictureAsPdfOutlined } from "@mui/icons-material";
import { Navigation, Pagination, Mousewheel, Keyboard } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { DataDialog, DataTooltip, Video } from "@app/components/atoms";
import {
  IconActionOverflow,
  IconDelete,
  IconEdit,
  IconInvalidUrl,
  IconLocationMachine,
  IconTick,
} from "@app/components/icons";
import { IconNotApplicable } from "@app/components/icons/IconNotApplicable/IconNotApplicable";
import CustomImageViewer from "@app/components/atoms/CustomImageViewer/CustomImageViewer";
import {
  INVALID_URL,
  MaintenanceReportType,
  MaintenanceResultType,
} from "@app/constants";
import { apiDeleteMachineReport } from "@app/api";
import { formatNumberWithCommas } from "@app/helpers";
import {
  setIsUpdatedMachineReportResponses,
  setShowAlert,
  store,
} from "@app/store";
import { CreateMaintenance } from "@app/pages/Features/MachineDetail/Components/Report/CreateReports/Maintenance/CreateMaintenance";
import type { Item, Report } from "@app/types";
import {
  ReportDialogSignatureFooter,
  ReportLastUpdatedHeader,
} from "./ReportSignatureSection";

type ReportWithMeta = Report & { isUpdated?: boolean };

type MaintenanceReportV3DialogProps = {
  open: boolean;
  report: ReportWithMeta | null;
  loading: boolean;
  groupId: string;
  machineId: string;
  machineModelType?: string;
  canEditOrDelete: boolean;
  localeCode: string;
  userTimeFormat: string;
  onClose: () => void;
  onUpdateSuccess: () => void;
  onRequestPdfOutput: () => void;
};

const tdStyle: React.CSSProperties = {
  padding: "8px",
  border: "1px solid #ddd",
  verticalAlign: "middle",
};

const tdRightStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: "center",
};

function toSignatureAtString(
  value: Date | string | null | undefined,
): string | null {
  return value == null ? null : String(value);
}

function itemHasDetail(item: Item): boolean {
  return (
    !!item.comment?.trim() ||
    (item.itemMedias?.length ?? 0) > 0 ||
    (item.itemRecordings?.length ?? 0) > 0
  );
}

function getCircleStyle(hasBorder = true): React.CSSProperties {
  return {
    border: hasBorder ? "2px solid #d3d3d3" : "none",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
  };
}

function getCircleStyleNotApplicable(
  hasBorder = true,
  stroke = "#D5D5D5",
): React.CSSProperties {
  return {
    backgroundColor: "transparent",
    border: hasBorder ? `2px solid ${stroke}` : "none",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
  };
}

function MaintenanceReportV3Skeleton() {
  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        marginTop: 12,
        border: "1px solid #ddd",
        tableLayout: "fixed",
      }}
    >
      <tbody>
        <tr>
          <td style={{ ...tdStyle, fontWeight: "bold" }}>
            <Skeleton variant="text" width={120} height={22} />
          </td>
          <td style={{ ...tdRightStyle, fontWeight: "bold" }}>
            <Skeleton variant="text" width={90} height={22} />
          </td>
        </tr>
        <tr>
          <td style={tdStyle}></td>
          <td style={tdRightStyle}>
            <Skeleton variant="rectangular" width={24} height={24} />
          </td>
        </tr>
        {Array.from({ length: 6 }).map((_, idx) => (
          <tr key={idx}>
            <td style={tdStyle}>
              <Skeleton variant="text" width="85%" height={18} />
            </td>
            <td style={tdRightStyle}>
              <Skeleton variant="text" width={60} height={18} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function MaintenanceReportV3Dialog({
  open,
  report,
  loading,
  groupId,
  machineId,
  machineModelType,
  canEditOrDelete,
  localeCode,
  userTimeFormat,
  onClose,
  onUpdateSuccess,
  onRequestPdfOutput,
}: MaintenanceReportV3DialogProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const overflowButtonRef = useRef<HTMLSpanElement>(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [activeDetailItem, setActiveDetailItem] = useState<Item | null>(null);
  const [brokenMediaKeys, setBrokenMediaKeys] = useState<Set<string>>(
    new Set(),
  );

  const [openLocation, setOpenLocation] = useState(false);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  useEffect(() => {
    setBrokenMediaKeys(new Set());
  }, [activeDetailItem]);

  const handleOpenOverflowMenu = () => {
    setMenuAnchorEl(overflowButtonRef.current);
  };
  const handleCloseOverflowMenu = () => setMenuAnchorEl(null);

  const handleEdit = () => {
    handleCloseOverflowMenu();
    setOpenEdit(true);
  };

  const confirmDelete = async () => {
    if (!groupId || !machineId || !report?.machineReportId) return;
    try {
      setIsDeleting(true);
      await apiDeleteMachineReport(groupId, machineId, report.machineReportId);
      store.dispatch(setShowAlert(t("alreadyDeleted")));
      dispatch(setIsUpdatedMachineReportResponses(`${new Date()}`));
      setOpenDelete(false);
      onClose();
    } catch (error) {
      console.error("Failed to delete maintenance report", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const renderDate = () => {
    if (!report?.reportedAt || isNaN(new Date(report.reportedAt).getTime())) {
      return "";
    }
    return format(new Date(report.reportedAt), userTimeFormat);
  };

  const renderTitle = () => {
    if (!report) return "";
    let title = "";

    if (
      report.regularMaintenanceItemChoice?.regularMaintenanceItemChoiceName?.trim()
    ) {
      title =
        report.regularMaintenanceItemChoice.regularMaintenanceItemChoiceName;
    } else {
      const intervals = (report.intervalMaintenanceItemChoices || []).map(
        (interval) => ({
          ...interval,
          intervalMaintenanceItemChoiceName:
            interval.intervalMaintenanceItemChoiceName ??
            t("announcementTypeValues.OTHER"),
        }),
      );
      const items = report.items || [];

      let maxInterval: (typeof intervals)[number] | null = null;
      let otherIntervals: string[] = [];

      if (intervals.length > 0) {
        maxInterval = intervals.reduce((max, curr) => {
          const currVal = parseInt(
            curr.intervalMaintenanceItemChoiceName.replace(/[^0-9]/g, ""),
            10,
          );
          const maxVal = parseInt(
            max.intervalMaintenanceItemChoiceName.replace(/[^0-9]/g, ""),
            10,
          );
          return currVal > maxVal ? curr : max;
        });
        otherIntervals = intervals
          .filter((i) => i !== maxInterval)
          .map((i) => i.intervalMaintenanceItemChoiceName);
      }

      const maxIntervalName = maxInterval
        ? `${maxInterval.intervalMaintenanceItemChoiceName} ${t("maintenanceReport")}`
        : "";

      const itemNames = items.map((item) =>
        item.maintenanceItemOther?.trim()
          ? item.maintenanceItemOther
          : item.maintenanceItemName,
      );

      if (maxIntervalName) {
        title +=
          otherIntervals.length > 0
            ? `${maxIntervalName} (${otherIntervals.join(", ")})`
            : maxIntervalName;
      }

      if (itemNames.length > 0) {
        title += (title ? ", " : "") + itemNames.join(", ");
      }
    }

    return title;
  };

  const title = renderTitle();
  const isTitleTruncated = title.length > 30;
  const displayTitle = isTitleTruncated ? `${title.slice(0, 30)}...` : title;

  return (
    <>
      <DataDialog
        open={open}
        onClose={onClose}
        disableBackdropClick={true}
        title={
          loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Skeleton variant="text" width="50%" height={30} />
            </Box>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                minWidth: 0,
              }}
            >
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                  flex: "1 1 auto",
                  minWidth: 0,
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                <DataTooltip
                  isTextPre
                  width="auto"
                  alwaysShow={isTitleTruncated}
                  tooltipContent={title || ""}
                >
                  {displayTitle || ""}
                </DataTooltip>
              </span>
            </div>
          )
        }
        action={
          !loading && canEditOrDelete ? (
            <span ref={overflowButtonRef}>
              <Button
                className="bnt-action-form"
                onClick={handleOpenOverflowMenu}
              >
                <IconActionOverflow />
              </Button>
            </span>
          ) : undefined
        }
        dialogActions={
          <ReportDialogSignatureFooter
            loading={loading}
            showSignatureInFooter
            signatureUrl={report?.signatureUrl}
            signatureBy={report?.signatureBy}
            signatureAt={toSignatureAtString(report?.signatureAt)}
            userTimeFormat={userTimeFormat}
          >
            <Button onClick={onClose} className="btn btn-no-border">
              {t("dialogRefuel.close")}
            </Button>
          </ReportDialogSignatureFooter>
        }
      >
        <div className="maintenance-report-v3-dialog">
          {!loading && report && (
            <ReportLastUpdatedHeader
              isUpdated={report.isUpdated}
              lastUpdatedSignatureUrl={report.lastUpdatedSignatureUrl}
              lastUpdatedSignatureBy={report.lastUpdatedSignatureBy}
              lastUpdatedSignatureAt={toSignatureAtString(
                report.lastUpdatedSignatureAt,
              )}
              userTimeFormat={userTimeFormat}
              marginLeft="4px"
            />
          )}

          {loading || !report ? (
            <MaintenanceReportV3Skeleton />
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: 12,
                border: "1px solid #ddd",
                tableLayout: "fixed",
              }}
            >
              <tbody>
                <tr>
                  <td style={{ ...tdStyle, fontWeight: "bold" }}>
                    {t("dialogMaintenanceReports.item")}
                  </td>
                  <td style={{ ...tdRightStyle, fontWeight: "bold" }}>
                    {report.workAt && !isNaN(new Date(report.workAt).getTime())
                      ? format(new Date(report.workAt), userTimeFormat)
                      : "-"}
                  </td>
                </tr>

                <tr>
                  <td style={tdStyle}></td>
                  <td style={tdRightStyle}>
                    <span
                      style={{ cursor: "pointer" }}
                      onClick={onRequestPdfOutput}
                    >
                      <PictureAsPdfOutlined
                        className="icon-pdf"
                        style={{ fontSize: "24px", color: "darkblue" }}
                      />
                    </span>
                  </td>
                </tr>

                {report.items.map((item, itemIndex) => {
                  const hasDetail = itemHasDetail(item);
                  const itemName =
                    item.maintenanceItemOther?.trim() ||
                    item.maintenanceItemName ||
                    "-";

                  return (
                    <tr key={`item-${itemIndex}`}>
                      <td style={tdStyle}>
                        <DataTooltip
                          isTextPre
                          width="100%"
                          tooltipContent={itemName}
                        >
                          <div
                            style={{
                              maxWidth: "100%",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {itemName}
                          </div>
                        </DataTooltip>
                      </td>
                      <td style={tdRightStyle}>
                        {item.result === MaintenanceResultType.PERFORMED ? (
                          <span
                            style={{
                              ...getCircleStyle(hasDetail),
                              cursor: hasDetail ? "pointer" : "default",
                              margin: "0 auto",
                            }}
                            onClick={() =>
                              hasDetail && setActiveDetailItem(item)
                            }
                          >
                            <IconTick fillColor="#0C7040" />
                          </span>
                        ) : item.result ===
                          MaintenanceResultType.NOT_PERFORMED ? (
                          <span
                            style={{
                              ...getCircleStyleNotApplicable(hasDetail),
                              cursor: hasDetail ? "pointer" : "default",
                              margin: "0 auto",
                            }}
                            onClick={() =>
                              hasDetail && setActiveDetailItem(item)
                            }
                          >
                            <IconNotApplicable />
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}

                <tr>
                  <td style={{ ...tdStyle, fontWeight: "bold" }}>
                    {t("tableMachineDetail.reporter")}
                  </td>
                  <td style={tdRightStyle}>
                    <DataTooltip isTextPre width="100%">
                      <div
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {localeCode === "ja"
                          ? `${report.surname} ${report.givenName}`
                          : `${report.givenName} ${report.surname}`}
                      </div>
                    </DataTooltip>
                  </td>
                </tr>

                {report.lat != null && report.lng != null && (
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: "bold" }}>
                      {t("tableMachineDetail.location")}
                    </td>
                    <td style={tdRightStyle}>
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => setOpenLocation(true)}
                      >
                        <IconLocationMachine />
                      </span>
                    </td>
                  </tr>
                )}

                {report.serviceMeterInHour != null && (
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: "bold" }}>
                      {t("serviceMeterOrSMR")}
                    </td>
                    <td style={tdRightStyle}>
                      <span dir="ltr">
                        {formatNumberWithCommas(report.serviceMeterInHour, "h")}
                      </span>
                    </td>
                  </tr>
                )}

                {report.odometer != null && (
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: "bold" }}>
                      {t("odometer")}
                    </td>
                    <td style={tdRightStyle}>
                      <span dir="ltr">
                        {formatNumberWithCommas(report.odometer, "km")}
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </DataDialog>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseOverflowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <IconEdit />
          </ListItemIcon>
          <ListItemText primary={t("formAction.edit")} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseOverflowMenu();
            setOpenDelete(true);
          }}
        >
          <ListItemIcon>
            <IconDelete />
          </ListItemIcon>
          <ListItemText
            primary={t("formAction.delete")}
            primaryTypographyProps={{ style: { color: "#DA093B" } }}
          />
        </MenuItem>
      </Menu>

      <DataDialog
        title={t("titleDeleteCard")}
        open={openDelete}
        setOpen={setOpenDelete}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("desDeleteCard")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenDelete(false)}
              className="btn btn-no-border"
              disabled={isDeleting}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              onClick={confirmDelete}
              className="btn-delete"
              disabled={isDeleting}
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <CreateMaintenance
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        groupId={groupId}
        machineId={machineId}
        machineModelType={machineModelType}
        maintenanceType={
          report?.isPeriodicMaintenance
            ? MaintenanceReportType.PERIODIC_MAINTENANCE
            : MaintenanceReportType.GENERAL_MAINTENANCE
        }
        mode="edit"
        editingReport={report as Report}
        onUpdateSuccess={onUpdateSuccess}
      />

      <DataDialog
        title={t("tableMachineDetail.location")}
        open={openLocation}
        setOpen={setOpenLocation}
        className="dialog-location-report"
        disableBackdropClick={true}
      >
        <iframe
          title="map-location"
          className="map-iframe"
          loading="lazy"
          src={`https://maps.google.com/maps?&q="+${report?.lat}, ${report?.lng}"&output=embed`}
        />
        <div className="flex justify-content-end">
          <Button
            onClick={() => setOpenLocation(false)}
            className="btn btn-no-border"
          >
            {t("dialogRefuel.close")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        title={
          activeDetailItem?.maintenanceItemOther?.trim() ||
          activeDetailItem?.maintenanceItemName ||
          ""
        }
        open={!!activeDetailItem}
        setOpen={() => setActiveDetailItem(null)}
        disableBackdropClick={true}
        dialogActions={
          <div className="flex justify-content-end">
            <Button
              onClick={() => setActiveDetailItem(null)}
              className="btn btn-no-border"
            >
              {t("dialogRefuel.close")}
            </Button>
          </div>
        }
      >
        {activeDetailItem && (
          <div className="content-report">
            {activeDetailItem.itemMedias.length > 0 && (
              <Swiper
                cssMode={true}
                navigation={true}
                pagination={true}
                mousewheel={true}
                keyboard={true}
                modules={[Navigation, Pagination, Mousewheel, Keyboard]}
                className="swiper mb-16"
              >
                {activeDetailItem.itemMedias.map((media: any, mIndex) => {
                  const mediaKey =
                    media?.machineReportMediaId ?? `media-${mIndex}`;
                  const isInvalidMediaUrl =
                    media?.mediaUrl === INVALID_URL ||
                    brokenMediaKeys.has(mediaKey);
                  const markBroken = () =>
                    setBrokenMediaKeys((prev) => new Set(prev).add(mediaKey));

                  return (
                    <SwiperSlide key={mediaKey}>
                      <div className="wrapper-file">
                        {isInvalidMediaUrl ? (
                          <Box
                            sx={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <IconInvalidUrl />
                          </Box>
                        ) : media?.mimeType?.includes("video") ? (
                          <Video media={media} onError={markBroken} />
                        ) : (
                          <div
                            className="wrapper-file"
                            onClick={() => {
                              if (media.mediaUrl) {
                                setSelectedImage(media.mediaUrl);
                                setViewerOpen(true);
                              }
                            }}
                            style={{ cursor: "zoom-in" }}
                          >
                            <Box
                              className="swiper-image img-report"
                              component="img"
                              alt="swiper-image"
                              src={media.mediaUrl}
                              onError={markBroken}
                            />
                          </div>
                        )}
                      </div>
                      <p
                        className="created-at txt-gray-1 fs-12"
                        style={{
                          textAlign: "right",
                          marginRight: "10px",
                          fontWeight: "bold",
                        }}
                      >
                        {media.captureMediaAt &&
                        !isNaN(new Date(media.captureMediaAt).getTime())
                          ? format(
                              new Date(
                                new Date(media.captureMediaAt).toLocaleString(
                                  "en-US",
                                  {
                                    timeZone:
                                      Intl.DateTimeFormat().resolvedOptions()
                                        .timeZone,
                                  },
                                ),
                              ),
                              userTimeFormat,
                            )
                          : ""}
                      </p>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            )}

            <div className="text-content pb-0">
              <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                {t("dialogReport.comment")}
              </p>
              <p className="txt-black-1 fs-16 mt-0 mb-2 txt-pre-wrap word-break">
                {activeDetailItem.comment &&
                activeDetailItem.comment.trim() !== ""
                  ? activeDetailItem.comment
                  : "-"}
              </p>
            </div>

            {(activeDetailItem.isRefill || activeDetailItem.isReplace) && (
              <div className="text-content pb-0">
                <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                  {t("dialogMaintenanceReports.action")}
                </p>
                <p className="txt-black-1 fs-16 mt-0 mb-2 txt-pre-wrap">
                  {activeDetailItem.isRefill
                    ? t("dialogMaintenanceReports.yesRefill")
                    : t("dialogMaintenanceReports.yesReplace")}
                </p>
              </div>
            )}

            {activeDetailItem.refillAmount != null && (
              <div className="text-content pb-0">
                <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap fw-500">
                  {t("dialogMaintenanceReports.refillAmount")}
                </p>
                <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                  <span dir="ltr">
                    {formatNumberWithCommas(activeDetailItem.refillAmount)} L
                  </span>
                </p>
              </div>
            )}

            {activeDetailItem.itemRecordings.length > 0 && (
              <div
                className="recordings-section"
                style={{ marginTop: 12, padding: 10 }}
              >
                {activeDetailItem.itemRecordings.map((rec: any, rIndex) => (
                  <div
                    key={rec?.machineReportRecordingId || `rec-${rIndex}`}
                    style={{ marginBottom: 16 }}
                  >
                    <audio
                      controls
                      src={rec.recordingUrl}
                      style={{ width: "100%" }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DataDialog>

      {viewerOpen && (
        <CustomImageViewer
          imageUrl={selectedImage}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </>
  );
}
